import { OnboardingReviewTools } from './OnboardingReviewTools';
// components/hr/OnboardingSessionDetailDrawer.tsx
import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Button,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/joy';
import {
  X as CloseOutlined,
  Calendar as CalendarTodayOutlined,
  CheckCircle as CheckCircleOutlined,
  Hourglass as HourglassEmptyOutlined,
  XCircle as CancelOutlined,
  PaperPlaneTilt as SendOutlined,
  Prohibit as BlockOutlined,
  User as PersonOutlined,
  FileText as DescriptionOutlined,
  ClockCounterClockwise as HistoryOutlined,
} from '@phosphor-icons/react';
import {
  useOnboardingSession,
  useOnboardingPackage,
  useResendOnboardingInvite,
  useApproveOnboarding,
  useRevokeOnboardingAccess,
} from '../../hooks/useHr';
import type { OnboardingSessionStatus } from '../../types/hr';
import toast from 'react-hot-toast';

interface OnboardingSessionDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  sessionId: string | null;
}

const statusColors: Record<OnboardingSessionStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'primary'> = {
  pending: 'neutral',
  in_progress: 'primary',
  awaiting_review: 'warning',
  completed: 'success',
  expired: 'danger',
  cancelled: 'danger',
};

const statusLabels: Record<OnboardingSessionStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  awaiting_review: 'Awaiting Review',
  completed: 'Completed',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

const activityIcons: Record<string, React.ReactNode> = {
  session_created: <PersonOutlined />,
  invitation_sent: <SendOutlined />,
  session_started: <HourglassEmptyOutlined />,
  step_started: <HourglassEmptyOutlined />,
  step_completed: <CheckCircleOutlined />,
  document_uploaded: <DescriptionOutlined />,
  document_signed: <DescriptionOutlined />,
  session_completed: <CheckCircleOutlined />,
  session_expired: <CancelOutlined />,
  session_cancelled: <CancelOutlined />,
  token_revoked: <BlockOutlined />,
  hr_review: <PersonOutlined />,
  note_added: <HistoryOutlined />,
};

const stepStatusLabels: Record<string, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
};

const formatFieldLabel = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

/**
 * Sensitive values (SSN/TIN, bank routing and account numbers) arrive already
 * masked from the API - render exactly what was sent, never reassemble it.
 */
const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(formatFieldValue).join(', ') : '-';
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const OnboardingSessionDetailDrawer: React.FC<OnboardingSessionDetailDrawerProps> = ({
  open,
  onClose,
  sessionId,
}) => {
  const { data: session, isLoading, error } = useOnboardingSession(sessionId || '');
  // The session's package summary carries counts only; the detail payload is
  // what gives each submitted step its title and ordering.
  const { data: onboardingPackage } = useOnboardingPackage(session?.package?.id || '');
  const resendInvite = useResendOnboardingInvite();
  const approveOnboarding = useApproveOnboarding();
  const revokeAccess = useRevokeOnboardingAccess();

  const handleResendInvite = async () => {
    if (!sessionId) return;
    try {
      await resendInvite.mutateAsync(sessionId);
      toast.success('Invitation resent successfully');
    } catch {
      toast.error('Failed to resend invitation');
    }
  };

  const handleApprove = async () => {
    if (!sessionId) return;
    try {
      await approveOnboarding.mutateAsync({ id: sessionId });
      toast.success('Onboarding approved! Team member is now active.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to approve onboarding');
    }
  };

  const handleRevokeAccess = async () => {
    if (!sessionId) return;
    if (!window.confirm('Are you sure you want to revoke access for this onboarding session?')) {
      return;
    }
    try {
      await revokeAccess.mutateAsync({ id: sessionId, reason: 'Revoked by administrator' });
      toast.success('Access revoked successfully');
      onClose();
    } catch {
      toast.error('Failed to revoke access');
    }
  };

  if (!sessionId) return null;

  // What the new hire actually entered, in package order. Steps the package no
  // longer defines still show up (at the end) so nothing submitted is hidden.
  const packageSteps = session?.configured_steps || onboardingPackage?.steps || [];
  const stepPositions = new Map(packageSteps.map((step, index) => [step.id, index]));
  const stepTitles = new Map(packageSteps.map((step) => [step.id, step.title]));
  const submittedSteps = Object.entries(session?.progress || {}).sort(
    ([a], [b]) =>
      (stepPositions.get(a) ?? Number.MAX_SAFE_INTEGER) - (stepPositions.get(b) ?? Number.MAX_SAFE_INTEGER)
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        content: {
          sx: {
            width: { xs: '100%', sm: 500 },
            p: 0,
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography level="title-lg">Onboarding Details</Typography>
        <IconButton variant="plain" onClick={onClose}>
          <CloseOutlined />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error || !session ? (
          <Typography color="danger" sx={{ textAlign: 'center', py: 4 }}>
            Failed to load session details
          </Typography>
        ) : (
          <>
            {/* Team Member Header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2 }}>
                {session.team_member.full_name.split(' ').map(n => n[0]).join('')}
              </Avatar>
              <Typography level="h4">{session.team_member.full_name}</Typography>
              <Typography level="body-sm" color="neutral">
                {session.team_member.email}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip size="sm" color={statusColors[session.status]} variant="soft">
                  {statusLabels[session.status]}
                </Chip>
              </Box>
            </Box>

            <OnboardingReviewTools key={session.id} session={session} />
            {/* Progress */}
            <Card variant="soft" sx={{ mb: 3 }}>
              <CardContent>
                <Typography level="title-sm" sx={{ mb: 1 }}>Completion Progress</Typography>
                <LinearProgress
                  determinate
                  value={session.completion_percentage}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography level="body-xs">{session.completion_percentage}% Complete</Typography>
                  <Typography level="body-xs" color="neutral">
                    Current Step: {session.current_step}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Package Info */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Onboarding Package</Typography>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography level="title-sm">{session.package?.name || 'Archived package'}</Typography>
                <Typography level="body-xs" color="neutral">
                  {packageSteps.length} steps | {session.package?.template_count || 0} documents
                </Typography>
              </CardContent>
            </Card>

            {/* Key Dates */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Key Dates</Typography>
            <List sx={{ mb: 3 }}>
              <ListItem>
                <ListItemDecorator><CalendarTodayOutlined /></ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-xs" color="neutral">Invitation Sent</Typography>
                  <Typography level="body-sm">
                    {session.invitation_sent_at
                      ? new Date(session.invitation_sent_at).toLocaleString()
                      : 'Not sent yet'}
                  </Typography>
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListItemDecorator><CalendarTodayOutlined /></ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-xs" color="neutral">Started</Typography>
                  <Typography level="body-sm">
                    {session.started_at
                      ? new Date(session.started_at).toLocaleString()
                      : 'Not started'}
                  </Typography>
                </ListItemContent>
              </ListItem>
              <ListItem>
                <ListItemDecorator><CalendarTodayOutlined /></ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-xs" color="neutral">Expires</Typography>
                  <Typography level="body-sm" color={session.is_expired ? 'danger' : undefined}>
                    {session.expires_at
                      ? new Date(session.expires_at).toLocaleString()
                      : 'No expiration'}
                    {session.is_expired && ' (Expired)'}
                  </Typography>
                </ListItemContent>
              </ListItem>
              {session.completed_at && (
                <ListItem>
                  <ListItemDecorator><CheckCircleOutlined color="var(--joy-palette-success-500)" /></ListItemDecorator>
                  <ListItemContent>
                    <Typography level="body-xs" color="neutral">Completed</Typography>
                    <Typography level="body-sm">
                      {new Date(session.completed_at).toLocaleString()}
                    </Typography>
                  </ListItemContent>
                </ListItem>
              )}
            </List>

            <Divider sx={{ my: 2 }} />

            {/* Submitted Information */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Submitted Information</Typography>
            {submittedSteps.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                {submittedSteps.map(([stepId, stepProgress]) => {
                  const fields = Object.entries(stepProgress?.data || {});
                  return (
                    <Card key={stepId} variant="outlined" sx={{ mb: 1.5 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography level="title-sm">
                            {stepTitles.get(stepId) || formatFieldLabel(stepId)}
                          </Typography>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={stepProgress?.status === 'completed' ? 'success' : 'neutral'}
                          >
                            {stepStatusLabels[stepProgress?.status] || 'Not Started'}
                          </Chip>
                        </Box>
                        {fields.length > 0 ? (
                          fields.map(([key, value]) => (
                            <Box
                              key={key}
                              sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}
                            >
                              <Typography level="body-xs" color="neutral">
                                {formatFieldLabel(key)}
                              </Typography>
                              <Typography level="body-sm" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                                {formatFieldValue(value)}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography level="body-xs" color="neutral">
                            Nothing entered for this step yet
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
                No information has been submitted yet
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Documents */}
            {session.documents && session.documents.length > 0 && (
              <>
                <Typography level="title-sm" sx={{ mb: 1 }}>Documents</Typography>
                <List sx={{ mb: 3 }}>
                  {session.documents.map((doc) => (
                    <ListItem key={doc.id}>
                      <ListItemDecorator><DescriptionOutlined /></ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm">{doc.name}</Typography>
                        <Typography level="body-xs" color="neutral">
                          {doc.document_type} - Uploaded {new Date(doc.created_at).toLocaleDateString()}
                        </Typography>
                      </ListItemContent>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {/* Activity Log */}
            <Typography level="title-sm" sx={{ mb: 1 }}>Activity Log</Typography>
            {session.activity_logs && session.activity_logs.length > 0 ? (
              <Box sx={{ pl: 2 }}>
                {session.activity_logs.map((log, index) => (
                  <Box key={log.id} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ pt: 0.5 }}>
                      {activityIcons[log.activity_type] || <HistoryOutlined />}
                    </Box>
                    <Box>
                      <Typography level="body-sm">{log.description}</Typography>
                      <Typography level="body-xs" color="neutral">
                        {new Date(log.created_at).toLocaleString()}
                        {log.performed_by_name && ` by ${log.performed_by_name}`}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography level="body-sm" color="neutral">
                No activity recorded yet
              </Typography>
            )}

            {/* Review Notes */}
            {session.review_notes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography level="title-sm" sx={{ mb: 1 }}>Review Notes</Typography>
                <Card variant="soft">
                  <CardContent>
                    <Typography level="body-sm">{session.review_notes}</Typography>
                    {session.reviewed_by && session.reviewed_at && (
                      <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                        Reviewed on {new Date(session.reviewed_at).toLocaleString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </Box>

      {/* Footer Actions */}
      {session && (
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Button variant="outlined" color="neutral" onClick={onClose}>
            Close
          </Button>
          {session.status === 'awaiting_review' && (
            <Button
              color="success"
              startDecorator={<CheckCircleOutlined />}
              onClick={handleApprove}
              loading={approveOnboarding.isPending}
            >
              Approve
            </Button>
          )}
          {['pending', 'in_progress'].includes(session.status) && (
            <>
              <Button
                variant="soft"
                startDecorator={<SendOutlined />}
                onClick={handleResendInvite}
                loading={resendInvite.isPending}
              >
                Resend Invite
              </Button>
              <Button
                variant="soft"
                color="danger"
                startDecorator={<BlockOutlined />}
                onClick={handleRevokeAccess}
                loading={revokeAccess.isPending}
              >
                Revoke Access
              </Button>
            </>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default OnboardingSessionDetailDrawer;
